import { type FoodUnit, gramsOfPieces, PIECE_PRESETS, piecesLabel } from '@gramas/shared'
import { Pill } from '@gramas/ui'

const PRESETS: Readonly<Record<FoodUnit, readonly number[]>> = {
  g: [30, 50, 100, 150, 200, 250],
  // Cup, glass, small bottle, can, big bottle — what drinks are actually sold in.
  ml: [100, 200, 250, 330, 500],
}

export interface QuantityPresetsProps {
  unit: FoodUnit
  value: number | null
  servingSizeG: number | null
  /** Set on a food people count: the chips become counts. */
  pieceWeightG?: number | null
  onPick: (quantity: number) => void
}

/**
 * One-tap quantities, with the current one visibly selected.
 *
 * The selection state is the point: without it the row reads as six buttons
 * that do nothing you can see, and after typing a number by hand there is
 * nothing tying the field to the chips.
 */
export function QuantityPresets({
  unit,
  value,
  servingSizeG,
  pieceWeightG,
  onPick,
}: QuantityPresetsProps) {
  // Nobody wants 30 g of egg. A counted food gets counts, and the gram field
  // above stays for the half-egg case.
  if (pieceWeightG != null && pieceWeightG > 0) {
    return (
      <div className="flex flex-wrap gap-2">
        {PIECE_PRESETS.map((count) => {
          const grams = gramsOfPieces(count, pieceWeightG)
          return (
            <Pill
              key={count}
              numeric
              active={value != null && Math.abs(value - grams) < 0.5}
              onClick={() => onPick(grams)}
            >
              {piecesLabel(count)}
            </Pill>
          )
        })}
      </div>
    )
  }

  const presets = PRESETS[unit]
  const serving = servingSizeG != null && !presets.includes(servingSizeG) ? servingSizeG : null

  return (
    <div className="flex flex-wrap gap-2">
      {presets.map((preset) => (
        <Pill key={preset} numeric active={value === preset} onClick={() => onPick(preset)}>
          {preset} {unit}
        </Pill>
      ))}
      {serving != null && (
        <Pill numeric active={value === serving} onClick={() => onPick(serving)}>
          1 porcija · {serving} {unit}
        </Pill>
      )}
    </div>
  )
}
